import React, { useState, useRef, useCallback } from 'react';

/**
 * ResizableImage — a React component that renders an image with
 * left/right drag handles for resizing. Click to select, drag to resize.
 */
export const ResizableImage = ({ src, alt, style = {}, maxHeight = 300, handleClick, onResize, initialWidth, initialHeight }) => {
    const [selected, setSelected] = useState(false);
    const [width, setWidth] = useState(initialWidth || null);
    const imgRef = useRef(null);
    const wrapperRef = useRef(null);

    const handleMouseDown = useCallback(
        (direction, e) => {
            e.preventDefault();
            e.stopPropagation();

            const startX = e.clientX;
            const startWidth = imgRef.current?.offsetWidth || 200;

            const onMouseMove = (ev) => {
                const diff = direction === 'left' ? startX - ev.clientX : ev.clientX - startX;
                const newWidth = Math.max(50, startWidth + diff);
                setWidth(newWidth);
            };

            const onMouseUp = () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';

                const finalWidth = imgRef.current?.offsetWidth;
                const finalHeight = imgRef.current?.offsetHeight;
                if (onResize && finalWidth) {
                    onResize({ width: finalWidth, height: finalHeight });
                }
            };

            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
        },
        [onResize]
    );

    // Close selection on click outside
    const handleWrapperClick = useCallback((e) => {
        handleClick && handleClick();
        e.stopPropagation();
        setSelected(true);
    }, []);

    // Deselect when clicking outside
    React.useEffect(() => {
        if (!selected) return;
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
                setSelected(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [selected]);

    return (
        <span
            ref={wrapperRef}
            className={`image-resizable-wrapper ${selected ? 'selected' : ''}`}
            onClick={handleWrapperClick}
            style={{ display: 'inline-block' }}
        >
            <img
                ref={imgRef}
                src={src}
                alt={alt || ''}
                style={{
                    maxWidth: '100%',
                    maxHeight,
                    objectFit: 'contain',
                    borderRadius: 4,
                    ...(width ? { width: `${width}px` } : {}),
                    ...style,
                }}
            />
            {selected && (
                <>
                    <span className="image-resize-handle handle-left" onMouseDown={(e) => handleMouseDown('left', e)} />
                    <span className="image-resize-handle handle-right" onMouseDown={(e) => handleMouseDown('right', e)} />
                </>
            )}
        </span>
    );
};
